import { Bell, Check, ChevronDown, IdCard, KeyRound, LayoutGrid, LogOut, Plug, Radar, ShieldAlert, Users } from "lucide-react";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { signOut } from "../lib/auth";
import { toast } from "./ui";
import type { AppId, Me } from "../lib/api";
import { activeOption, useConnections } from "../lib/connections";
import { cx } from "./ui";

export function Wordmark({ className = "h-6" }: { className?: string }) {
  return (
    <>
      <img src="/brand/intellias-logo.png" alt="Intellias" className={cx("wordmark-light w-auto", className)} />
      <img src="/brand/intellias-logo-white.png" alt="Intellias" className={cx("wordmark-dark w-auto", className)} />
    </>
  );
}

/**
 * Portal frame (design §7): Intellias header, tenant name (the logo slot comes with tenant branding), connection
 * picker, notifications bell and profile menu; "Powered by Intellias" is always shown.
 */
export function Shell({ me, children }: { me: Me; children: ReactNode }) {
  const tenantSide = me.kind === "tenant";
  const currentApp = useCurrentApp();
  const inApp = tenantSide && currentApp !== null;
  const nav = [
    tenantSide && { to: "/", label: "Apps", icon: LayoutGrid, end: true },
    tenantSide && me.tenantAdmin && { to: "/admin/users", label: "Users", icon: Users },
    tenantSide && me.tenantAdmin && { to: "/admin/connections", label: "Connections", icon: Plug },
    tenantSide && me.tenantAdmin && { to: "/admin/attributes", label: "Attributes", icon: IdCard },
    tenantSide && me.tenantAdmin && { to: "/admin/sign-in", label: "Sign-in", icon: KeyRound },
    tenantSide && me.tenantAdmin && me.tenant?.apps.includes("radar") && { to: "/admin/radar", label: "RADAR", icon: Radar },
    !tenantSide && { to: "/", label: "Tenants", icon: LayoutGrid, end: true },
  ].filter(Boolean) as { to: string; label: string; icon: typeof Users; end?: boolean }[];

  return (
    <div className="min-h-full flex flex-col">
      <div className="brand-bar h-1" aria-hidden />
      <header className="bg-surface border-b border-line">
        <div className="max-w-7xl mx-auto px-4 h-14 flex items-center gap-4">
          <Wordmark />
          <span className="text-line" aria-hidden>
            |
          </span>
          <span className="font-display font-semibold text-ink whitespace-nowrap shrink-0">{tenantSide ? me.tenant!.name : "Platform console"}</span>
          {/* Inside an app the connection picker takes the room the labels need (six admin items ran under it), so
              the menu is icons with tooltips there; it's clipped rather than ever drawn under the picker. */}
          <nav className="hidden sm:flex items-center gap-1 ml-2 min-w-0 overflow-hidden" aria-label="Main">
            {nav.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} title={n.label} className={({ isActive }) => cx("inline-flex items-center gap-1.5 px-3 h-8 rounded-lg text-sm whitespace-nowrap shrink-0", isActive ? "bg-accent-soft text-accent-text font-medium" : "text-ink-2 hover:bg-surface-2")}>
                <n.icon className="size-4" aria-hidden /> <span className={inApp ? "sr-only" : "hidden lg:inline"}>{n.label}</span>
              </NavLink>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2 shrink-0">
            {tenantSide && <ConnectionPicker />}
            <button type="button" aria-label="Notifications" className="size-9 inline-flex items-center justify-center rounded-lg text-ink-2 hover:bg-surface-2">
              <Bell className="size-4" aria-hidden />
            </button>
            <ProfileMenu me={me} />
          </div>
        </div>
      </header>
      {tenantSide && <ProductionBanner />}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 py-6">{children}</main>
      <footer className="border-t border-line text-xs text-muted">
        <div className="max-w-7xl mx-auto px-4 h-12 flex items-center">Powered by Intellias</div>
      </footer>
    </div>
  );
}

function ProfileMenu({ me }: { me: Me }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const initials = (me.name || me.email).split(/[\s@.]+/).slice(0, 2).map((s) => s[0]?.toUpperCase()).join("");
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="inline-flex items-center gap-1 h-9 pl-1 pr-2 rounded-lg hover:bg-surface-2">
        <span className="size-7 rounded-full bg-accent-chip text-accent-text text-xs font-semibold inline-flex items-center justify-center">{initials}</span>
        <ChevronDown className="size-4 text-muted" aria-hidden />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-64 rounded-xl border border-line bg-surface shadow-lg p-2 z-20">
          <div className="px-2 py-1.5">
            <div className="text-sm font-medium text-ink truncate">{me.name || me.email}</div>
            <div className="text-xs text-muted truncate">{me.email}</div>
          </div>
          <button type="button" onClick={() => void signOut()} className="w-full flex items-center gap-2 px-2 h-9 rounded-lg text-sm text-ink-2 hover:bg-surface-2">
            <LogOut className="size-4" aria-hidden /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

/** The app the user is in, from /apps/<appId>/…; the picker only shows inside an app. */
function useCurrentApp(): AppId | null {
  const m = /^\/apps\/([a-z]+)/.exec(useLocation().pathname);
  return (m?.[1] as AppId | undefined) ?? null;
}

/** Design §5.3: the active connection for the current app, chosen from the user's usable connections. */
function ConnectionPicker() {
  const app = useCurrentApp();
  const { apps, setActive, reload } = useConnections();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => !ref.current?.contains(e.target as Node) && setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);
  const c = app ? apps[app] : undefined;
  if (!app || !c) return null;
  const active = activeOption(c);
  const choose = async (connId: string) => {
    try {
      await setActive(app, connId);
      setOpen(false);
    } catch (e) {
      toast((e as Error).message, "error");
    }
  };
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => {
          if (!open) void reload(); // fresh choices each time the picker opens
          setOpen((o) => !o);
        }}
        aria-expanded={open}
        title={active ? `${active.name} (${active.envLabel})` : undefined} className={cx("inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border text-sm whitespace-nowrap max-w-[18rem]", active?.production ? "border-bad text-bad" : active ? "border-line text-ink-2" : "border-warn text-warn")}
      >
        <Plug className="size-4 shrink-0" aria-hidden />
        <span className="truncate">{active ? `${active.name} · ${active.envLabel}` : "Choose a connection"}</span>
        <ChevronDown className="size-4 shrink-0" aria-hidden />
      </button>
      {open && (
        <div className="absolute right-0 mt-1 w-80 rounded-xl border border-line bg-surface shadow-lg p-2 z-20" role="menu">
          <div className="px-2 py-1 text-xs text-muted">Infor connection for {c.name}</div>
          {c.options.length === 0 && <div className="px-2 py-2 text-sm text-muted">No connections are available to you for this app. Ask your administrator.</div>}
          {c.options.map((o) => (
            <button
              key={o.connId}
              type="button"
              role="menuitem"
              disabled={!o.usable}
              title={o.reason}
              onClick={() => void choose(o.connId)}
              className="w-full flex items-start gap-2 px-2 py-2 rounded-lg text-left text-sm hover:bg-surface-2 disabled:opacity-50 disabled:hover:bg-transparent"
            >
              <Check className={cx("size-4 mt-0.5 shrink-0", o.connId === c.active ? "text-accent" : "invisible")} aria-hidden />
              <span className="flex-1 min-w-0">
                <span className="flex items-center gap-2">
                  <span className="text-ink truncate">{o.name}</span>
                  <span className={cx("text-xs rounded px-1.5 py-0.5 font-semibold", o.production ? "bg-bad text-white" : "bg-accent-chip text-accent-text")}>{o.envLabel}</span>
                </span>
                {o.reason && <span className="block text-xs text-muted">{o.reason}</span>}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Red PRD banner while the current app works against a production connection (design §5.2). */
function ProductionBanner() {
  const app = useCurrentApp();
  const { apps } = useConnections();
  const active = app ? activeOption(apps[app]) : null;
  if (!active?.production) return null;
  return (
    <div className="bg-bad text-white text-sm" role="status">
      <div className="max-w-7xl mx-auto px-4 h-9 flex items-center gap-2">
        <ShieldAlert className="size-4" aria-hidden /> Production: {active.name} ({active.envLabel}). Changes here affect live Infor data.
      </div>
    </div>
  );
}

